import { Component, type ErrorInfo, type ReactNode } from "react";
import { compileErrorBus } from "@/lib/compileErrorBus";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Reports any React render-time error to the compileErrorBus so the
 * DevCompileErrorOverlay (and the admin panel) can show file/line/symbol info.
 */
export default class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const stack = error.stack ?? "";
    const componentStack = info.componentStack ?? "";
    // Try to extract first src/... frame
    const fileMatch =
      stack.match(/((?:src|app|pages)\/[\w./\-@]+\.(?:tsx?|jsx?)):(\d+):(\d+)/) ??
      componentStack.match(/in (\w+) \(at ([\w./\-@]+):(\d+)\)/);
    let file: string | undefined;
    let line: number | undefined;
    let column: number | undefined;
    if (fileMatch) {
      if (fileMatch[0].startsWith("in ")) {
        file = fileMatch[2];
        line = Number(fileMatch[3]);
      } else {
        file = fileMatch[1];
        line = Number(fileMatch[2]);
        column = Number(fileMatch[3]);
      }
    }
    compileErrorBus.report({
      kind: "runtime",
      message: error.message,
      file,
      line,
      column,
      frame: componentStack.trim().split("\n").slice(0, 6).join("\n"),
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="min-h-screen flex items-center justify-center p-8 bg-background">
            <div className="max-w-lg text-center space-y-3">
              <h1 className="font-serif text-3xl text-foreground">
                Er ging iets mis
              </h1>
              <p className="text-muted-foreground">
                Er is een onverwachte fout opgetreden. Herlaad de pagina om
                opnieuw te proberen.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 rounded-full bg-foreground text-background px-5 py-2 text-sm hover:bg-foreground/90"
              >
                Pagina herladen
              </button>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
